// @ts-nocheck
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block";
import { infoProps } from "@store/Themes/common/props/info";
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext } from "react";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { commonGridItemStyles } from "@store/Themes/common/mixins/checkout";
import { orderDataLabel, orderDataValue } from "@store/Themes/common/props/checkout";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";
/* Store Custom Code - End */

// const Date = getWidgetComponent("OrderDetails/OrderDate"); // Store Custom Code
const Notes = getWidgetComponent("OrderDetails/OrderDetailsNotes");
const Status = getWidgetComponent("OrderDetails/OrderDetailsStatus");
const Salesperson = getWidgetComponent("OrderDetails/OrderSalesperson");
const RequestedDate = getWidgetComponent("OrderDetails/OrderDetailsRequestedDate");
const PaymentTerms = getWidgetComponent("OrderDetails/OrderDetailsPaymentTerms");
const PoNumber = getWidgetComponent("OrderDetails/OrderPo");
const ShippingAddress = getWidgetComponent("OrderDetails/OrderDetailsShippingAddress");
const BillingAddress = getWidgetComponent("OrderDetails/OrderDetailsBillingAddress");
const ShippingMethod = getWidgetComponent("OrderDetails/OrderDetailsShippingMethod");
const ShipmentPackages = getWidgetComponent("OrderDetails/OrderDetailsShipmentPackages");
const VatNumber = getWidgetComponent("OrderDetails/OrderDetailsVatNumber");
const VmiLocation = getWidgetComponent("OrderDetails/OrderDetailsVmiLocation");

/* Store Custom Code - Start */
const OrderDate = getWidgetComponent("OrderDetails/OrderDate");
/* Store Custom Code - End */

interface Props extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
}

export interface OrderDetailsInformationStyles {
    block?: BlockProps;
    orderNumberHeading?: TypographyProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    notesGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    billingInformationTitle?: TypographyProps;
    // Store Custom Code
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    fullGridItem?: GridItemProps;
    demiGridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    routingTypeHighlighted?: InjectableCss;
}

export const orderDetailsInformationStyles: OrderDetailsInformationStyles = {
    /* Store Custom Code - Start */
    wrapper: {
        css: css`
            ${commonGridItemStyles()}
            padding-top: 2rem;

            @media print {
                border-bottom: none;
            }
        `,
    },
    gridContainer: { gap: 32 },
    fullGridItem: { width: 12 },
    demiGridItem: { width: [12, 6, 6, 6, 6] },
    smallGridItem: { width: [6, 4, 4, 4, 4] },
    infoLabel: orderDataLabel,
    infoValue: orderDataValue,
    routingTypeHighlighted: {
        ...orderDataValue,
        css: css`
            background: ${getColor("warning")};
            color: ${getColor("warning.contrast")};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            padding: 0 ${pxToRem(2)};
        `,
    },
    /* Store Custom Code - End */
};

const styles = orderDetailsInformationStyles;

const OrderDetailsInformation: FC<Props> = ({ orderState: baseOrderState }) => {
    const { orderSettings, productSettings } = useSettingsCollection();
    const orderStateContext = useContext(OrderStateContext);
    const orderState = baseOrderState || orderStateContext;
    // const order = orderState.value; // Store Custom Code
    const { value: order, isLoading } = useContext(OrderStateContext); // Store Custom Code

    /* Store Custom Code - Start */
    if (!order || isLoading) {
        return null;
    }
    /* Store Custom Code - End */

    const showPoNumber = orderSettings.showPoNumber && order.customerPO;
    const showVatNumber = productSettings.enableVat && order.customerVatNumber;
    const showTerms = orderSettings.showTermsCode && order.terms;
    const showInformations =
        order.orderDate ||
        showPoNumber ||
        order.statusDisplay ||
        order.salesperson ||
        showVatNumber ||
        showTerms ||
        order.vmiLocationName ||
        order.notes; // Store Custom Code

    /* Store Custom Code - Start */
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.gridContainer}>
                    {order.orderDate && (
                        <GridItem {...styles.smallGridItem}>
                            <OrderDate />
                        </GridItem>
                    )}
                    {order.customerPO && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("PO Number")}</Typography>
                                <Typography {...styles.infoValue}>{order.customerPO}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.shipViaDescription && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("Shipping Service")}</Typography>
                                <Typography {...styles.infoValue}>{order.shipViaDescription}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.properties.createdBy && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("Created by")}</Typography>
                                <Typography {...styles.infoValue}>{order.properties.createdBy}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.properties.originalReleaseDate && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("Original Release Date")}</Typography>
                                <Typography {...styles.infoValue}>
                                    <LocalizedDateTime
                                        dateTime={new Date(order.properties.originalReleaseDate)}
                                        options={{ year: "numeric", month: "numeric", day: "numeric", timeZone: "UTC" }}
                                    />
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.properties.shipService && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("Ship-Freight Service")}</Typography>
                                <Typography {...styles.infoValue}>{order.properties.shipService}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.status && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("Status")}</Typography>
                                <Typography {...styles.infoValue}>{order.statusDisplay}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.properties.attentionOf && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>{translate("Attention of")}</Typography>
                                <Typography {...styles.infoValue}>{order.properties.attentionOf}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.properties.docTypeCode && order.properties.docTypeDescription && (
                        <GridItem {...styles.smallGridItem}>
                            <StyledWrapper>
                                <Typography {...styles.infoLabel}>
                                    {translate("Provenance of order (doc type)")}
                                </Typography>
                                <Typography {...styles.infoValue}>
                                    {`${order.properties.docTypeCode} - ${order.properties.docTypeDescription}`}
                                </Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {order.notes && (
                        <GridItem {...styles.fullGridItem}>
                            <Notes />
                        </GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...styles.demiGridItem}>
                        <BillingAddress />
                    </GridItem>
                    <GridItem {...styles.demiGridItem}>
                        <ShippingAddress />
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </>
    );
    /* Store Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: OrderDetailsInformation,
    definition: {
        group: "Order Details",
        displayName: "Order Details Information", // Store Custom Code
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
    },
};

export default widgetModule;
